const env = import.meta.env;
const missing: string[] = [];

const readEnv = (key: string): string => {
  const value = env[key];
  if (value === undefined) {
    missing.push(key);
    return '';
  }
  return String(value);
};

const readEnvNumber = (key: string, fallback: number): number => {
  const raw = readEnv(key);
  if (raw === '') return fallback;
  const num = Number(raw);
  return Number.isNaN(num) ? fallback : num;
};

const readEnvBool = (key: string): boolean => {
  return readEnv(key) === 'true';
};

const readEnvOptional = (key: string): string => {
  const value = env[key];
  if (value === undefined) return '';
  return String(value);
};

const makeConfig = () => ({
  publicUrl: readEnv('VITE_PUBLIC_URL'),
  env: readEnv('VITE_ENV'),
  version: readEnv('VITE_VERSION'),
  api: {
    commons: readEnv('VITE_API_COMMONS'),
    bff: readEnv('VITE_API_BFF'),
    bankingGateway: readEnv('VITE_BANKING_GATEWAY_URL'),
    lending: readEnv('VITE_LENDING_API_URL'),
    lendingPartner: readEnv('VITE_LENDING_PARTNER_URL'),
    notificationsV2: readEnv('VITE_NOTIFICATIONS_V2_URL'),
  },
  nexo: {
    clientId: readEnv('VITE_TIENDANUBE_CLIENT_ID'),
    debug: readEnv('VITE_NEXO_LOG') === 'DEBUG',
  },
  amplitude: {
    apiKey: readEnv('VITE_AMPLITUDE_API_KEY'),
  },
  fingerprint: {
    appKey: readEnv('VITE_FINGERPRINT_APP_KEY'),
  },
  satismeter: {
    writeKey: readEnv('VITE_SATISMETER_WRITE_KEY'),
    brWriteKey: readEnvOptional('VITE_SATISMETER_BR_WRITE_KEY'),
  },
  kyc: {
    analysisDays: readEnvNumber('VITE_KYC_ANALYSIS_DAYS', 3),
    defaultUpdatedStatus: readEnv('VITE_KYC_DEFAULT_UPDATED_STATUS'),
  },
  documents: {
    maxSize: readEnvNumber('VITE_MAX_DOCUMENT_SIZE', 7_000_000),
    maxMxSize: readEnvNumber('VITE_MAX_DOCUMENT_MX_SIZE', 10_485_760),
    maxMxSizeSecondDocument: readEnvNumber(
      'VITE_MAX_DOCUMENT_MX_SIZE_SECOND_DOCUMENT',
      5_242_880,
    ),
    maxChargebackSizeDefault: readEnvNumber(
      'VITE_MAX_CHARGEBACK_DOCUMENT_SIZE_DEFAULT',
      716_800,
    ),
    maxChargebackSizeMx: readEnvNumber(
      'VITE_MAX_CHARGEBACK_DOCUMENT_SIZE_MX',
      4_194_304,
    ),
  },
  reports: {
    minReportDay: readEnv('VITE_MIN_REPORT_DAY'),
    minTransactionDetailDay: readEnv('VITE_MIN_TRANSACTION_DETAIL_DAY'),
  },
  statementReport: {
    useMocks: readEnvBool('VITE_USE_STATEMENT_MOCKS'),
  },
  apollo: {
    collectCompanyDocumentFormUrl: readEnv(
      'VITE_APOLLO_COLLECT_COMPANY_DOCUMENT_FORM_URL',
    ),
    collectIndividualDocumentFormUrl: readEnv(
      'VITE_APOLLO_COLLECT_INDIVIDUAL_DOCUMENT_FORM_URL',
    ),
    rolloutAcknowledgmentFormUrl: readEnv(
      'VITE_APOLLO_ROLLOUT_ACKNOWLEDGMENT_FORM_URL',
    ),
  },
  aiprise: {
    templateId: readEnv('VITE_AIPRISE_TEMPLATE_ID'),
    mode: readEnv('VITE_AIPRISE_MODE'),
  },
  lending: {
    clarityProjectId: readEnvOptional('VITE_LENDING_CLARITY_ID'),
  },
});

export type Config = ReturnType<typeof makeConfig>;

const builtConfig = makeConfig();

if (missing.length > 0) {
  throw new Error(
    `The following environment variables are not defined: "${missing.join(
      '", "',
    )}"`,
  );
}

export const config = builtConfig;
